import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useView } from '../../contexts/ViewContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import PageSwitch from '../../components/common/PageSwitch';
import ContractPricingTab from '../../components/purchasing/tabs/ContractPricingTab';
import RelationshipContractsTable from './RelationshipContractsTable';
import LeadTimesPage from '../lead-times/LeadTimesPage';

function AgreementsPage({ readOnly }: { readOnly: boolean }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Contracts</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          {readOnly
            ? 'The agreements your customer holds with your company.'
            : 'Every supplier agreement in one place — end dates, notice periods, owners and review cadence. Agreements are recorded on the supplier\'s profile; the expiry and notice-period reminders come from these dates.'}
        </p>
      </div>
      <RelationshipContractsTable readOnly={readOnly} />
    </div>
  );
}

function PricebooksPage({ readOnly }: { readOnly: boolean }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Pricebooks</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          {readOnly
            ? 'The rate cards your customer has shared with you.'
            : 'The rate card under each contract: part, unit or lot price, quantity band and validity. Price Variance compares invoices against these.'}
        </p>
      </div>
      <ContractPricingTab />
    </div>
  );
}

/**
 * Contracts — agreements, pricebooks and the SLA lead-time register, one
 * sidebar item. A supplier sees its own agreements, shared pricebooks and the
 * lead times it is measured against, read-only.
 */
export default function ContractsPage() {
  const { portalMode } = useView();
  const { isMenuItemVisible } = useMenuSettings();
  const [params, setParams] = useSearchParams();
  const readOnly = portalMode === 'supplier';

  // `?contract=<id>` (calendar and price-variance links) names a pricebook, so
  // name the tab in the URL before the pricebook tab consumes the id —
  // otherwise the switch falls back to Agreements the moment it is cleared.
  const deepLinksToPricebook = params.has('contract') && !params.has('tab');
  useEffect(() => {
    if (!deepLinksToPricebook) return;
    const next = new URLSearchParams(params);
    next.set('tab', 'pricebooks');
    setParams(next, { replace: true });
  }, [deepLinksToPricebook, params, setParams]);

  const leadTimesOn = isMenuItemVisible(readOnly ? 'supplierLeadTimes' : 'leadTimes');
  const tabs = [
    { value: 'agreements', label: 'Agreements', element: <AgreementsPage readOnly={readOnly} /> },
    { value: 'pricebooks', label: 'Pricebooks', element: <PricebooksPage readOnly={readOnly} /> },
    { value: 'lead-times', label: readOnly ? 'My Lead Times' : 'Lead Times', element: <LeadTimesPage />, visible: leadTimesOn },
  ];
  return <PageSwitch testId="contracts-tabs" tabs={tabs} defaultValue={deepLinksToPricebook ? 'pricebooks' : 'agreements'} />;
}
